import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, SwitchCamera, ChevronDown, Wand2, SlidersHorizontal, Mic, MicOff, Drama } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BEAUTY_DEFAULTS, createBeautyPipeline } from "@/lib/beautyEngine";
import BeautyTray from "@/components/BeautyTray";

const CATEGORIES = ["Music", "Dance", "Comedy", "Astro", "PK Battle", "Chat"];

export default function GoLive() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Chat");
  const [busy, setBusy] = useState(false);
  const [visibility, setVisibility] = useState("Public");
  const prefs = user?.beauty_prefs;
  const [params, setParams] = useState(() => ({ ...BEAUTY_DEFAULTS, ...(prefs?.params || {}) }));
  const [presetKey, setPresetKey] = useState(prefs?.preset || "none");
  const [sticker, setSticker] = useState(prefs?.sticker || "none");
  const [makeup, setMakeup] = useState(prefs?.makeup || "none");
  const [mirrorOn, setMirrorOn] = useState(prefs?.mirror !== undefined ? prefs.mirror : true);
  const [trayTab, setTrayTab] = useState(null);
  const [micOn, setMicOn] = useState(true);
  const stateRef = useRef({ params: { ...BEAUTY_DEFAULTS, ...(prefs?.params || {}) }, presetKey: prefs?.preset || "none", sticker: prefs?.sticker || "none", makeup: prefs?.makeup || "none" });
  const previewRef = useRef(null);
  const pipelineRef = useRef(null);

  const pickParams = (p) => {
    stateRef.current.params = p;
    setParams(p);
  };
  const pickPreset = (k) => {
    stateRef.current.presetKey = k;
    setPresetKey(k);
  };
  const pickSticker = (k) => {
    stateRef.current.sticker = k;
    setSticker(k);
  };
  const pickMakeup = (k) => {
    stateRef.current.makeup = k;
    setMakeup(k);
  };
  const [camDenied, setCamDenied] = useState(false);
  const [facing, setFacing] = useState("user");
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopCam = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startCam = useCallback(async (mode) => {
    stopCam();
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode }, audio: false });
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        videoRef.current.play().catch((e) => console.warn("Preview autoplay blocked, engine will retry", e));
      }
      setCamDenied(false);
    } catch {
      setCamDenied(true);
    }
  }, [stopCam]);

  useEffect(() => {
    startCam(facing);
    return stopCam;
  }, [facing, startCam, stopCam]);

  useEffect(() => {
    const videoEl = videoRef.current;
    const holder = previewRef.current;
    if (!videoEl || !holder || pipelineRef.current) return;
    const pipeline = createBeautyPipeline(videoEl, () => stateRef.current);
    pipelineRef.current = pipeline;
    const c = pipeline.canvas;
    c.style.width = "100%";
    c.style.height = "100%";
    c.style.objectFit = "cover";
    holder.appendChild(c);
    return () => {
      pipeline.stop();
      c.remove();
      pipelineRef.current = null;
    };
  }, []);

  const start = async () => {
    setBusy(true);
    try {
      const t = title.trim() || `Live with ${user?.name?.split(" ")[0] || "me"}`;
      const { data } = await api.post("/streams/go-live", { title: t, category, tags: [category.toLowerCase()], visibility: visibility.toLowerCase() });
      api.patch("/users/me/beauty-prefs", { params, preset: presetKey, sticker, makeup, mirror: mirrorOn }).catch((e) => console.warn("Beauty prefs autosave failed", e));
      stopCam();
      toast.success("You are LIVE! 🔴");
      navigate(`/stream/${data.stream.id}`, { state: { agora: { ...data.agora, is_host: true }, mic_muted: !micOn, beauty: params, filter: presetKey, sticker, makeup, mirror: mirrorOn } });
    } catch (e) {
      toast.error(errText(e));
      setBusy(false);
    }
  };

  const tools = [
    { key: "beauty", label: "Beauty", icon: Wand2, active: trayTab === "beauty" || Object.values(params).some((v) => v > 0), onClick: () => setTrayTab((t) => (t === "beauty" ? null : "beauty")) },
    { key: "filters", label: "Filters", icon: SlidersHorizontal, active: trayTab === "filters" || presetKey !== "none", onClick: () => setTrayTab((t) => (t === "filters" ? null : "filters")) },
    { key: "mic", label: "Mic", icon: micOn ? Mic : MicOff, active: micOn, onClick: () => { setMicOn((m) => !m); toast(micOn ? "Mic will start muted" : "Mic on", { duration: 1200 }); } },
    { key: "effects", label: "Effects", icon: Drama, active: trayTab === "ar" || sticker !== "none" || makeup !== "none", onClick: () => setTrayTab((t) => (t === "ar" ? null : "ar")) },
    { key: "flip", label: "Flip", icon: SwitchCamera, active: false, onClick: () => setFacing((f) => (f === "user" ? "environment" : "user")), testid: "go-live-flip" },
  ];

  return (
    <div className="h-screen-dvh max-w-md mx-auto relative overflow-hidden bg-[#0B0B14]" data-testid="go-live-page">
      {camDenied ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center px-8">
          <div className="glass rounded-3xl px-8 py-8 text-center">
            <p className="text-4xl mb-3">📷</p>
            <p className="text-white text-sm font-bold">Camera preview unavailable</p>
            <p className="text-[#6E6683] text-xs mt-1">Allow camera access — you can still go live below</p>
          </div>
        </div>
      ) : (
        <>
          <video ref={videoRef} autoPlay playsInline muted className="fixed top-0 left-0 w-px h-px opacity-0 pointer-events-none" />
          <div ref={previewRef} data-testid="golive-preview-canvas"
            className="absolute inset-0 w-full h-full"
            style={{ transform: facing === "user" && mirrorOn ? "scaleX(-1)" : "none" }} />
        </>
      )}
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black/80 via-black/35 to-transparent pointer-events-none" />

      {/* Top: close + title + visibility, then category chips */}
      <div className="absolute top-0 inset-x-0 z-20 px-3 pt-4">
        <div className="flex items-center gap-2">
          <button data-testid="go-live-close" onClick={() => { stopCam(); navigate(-1); }}
            className="w-10 h-10 rounded-full glass-dark flex items-center justify-center text-white shrink-0"><X size={17} /></button>
          <div className="glass-dark rounded-2xl flex-1 min-w-0 flex items-center gap-2 pl-4 pr-1.5 py-1.5">
            <input data-testid="stream-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Add a title… ✨"
              className="flex-1 bg-transparent outline-none text-white text-sm placeholder:text-white/50 min-w-0 py-1.5" />
            <button data-testid="visibility-toggle" onClick={() => setVisibility((v) => (v === "Public" ? "Followers" : "Public"))}
              className="shrink-0 flex items-center gap-1 glass-chip px-3 py-1.5 text-white text-[11px] font-bold">
              {visibility} <ChevronDown size={11} />
            </button>
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar mt-3" data-testid="category-row">
          {CATEGORIES.map((c) => (
            <button key={c} data-testid={`category-${c.toLowerCase().replace(" ", "-")}`} onClick={() => setCategory(c)}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all ${category === c ? "glass-chip-active" : "glass-chip text-white/80"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Left vertical toolbar */}
      <div className="absolute left-3 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-3.5" data-testid="tools-row">
        {tools.map((t) => (
          <button key={t.key} data-testid={t.testid || `tool-${t.key}`} onClick={t.onClick}
            className="flex flex-col items-center gap-1 active:scale-90 transition-transform">
            <span className={`w-11 h-11 rounded-full glass-dark flex items-center justify-center ${t.active ? "ring-1 ring-[#FFD86B]/70 shadow-[0_0_12px_rgba(255,216,107,0.35)]" : ""}`}>
              <t.icon size={17} className="text-white" />
            </span>
            <span className="text-white text-[10px] font-semibold drop-shadow">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Bottom: tray + GO LIVE orb */}
      <div className="absolute inset-x-0 bottom-0 z-20 pb-6 flex flex-col items-center">
        {trayTab && (
          <div className="w-full px-3 mb-4" data-testid="beauty-panel">
            <BeautyTray key={trayTab} initialTab={trayTab}
              params={params} onParams={pickParams}
              presetKey={presetKey} onPreset={pickPreset}
              sticker={sticker} onSticker={pickSticker}
              makeup={makeup} onMakeup={pickMakeup}
              mirrored={mirrorOn} onMirror={() => setMirrorOn((m) => !m)}
              testPrefix="golive-" />
          </div>
        )}
        <button data-testid="start-stream-button" onClick={start} disabled={busy}
          className="rounded-full p-[3px] active:scale-95 transition-transform disabled:opacity-60"
          style={{ background: "conic-gradient(from 0deg, #FFD86B, #FF2E97, #A03CF5, #FFD86B)", boxShadow: "0 0 36px rgba(255,46,151,0.55)" }}>
          <span className="w-[82px] h-[82px] rounded-full flex items-center justify-center text-white font-extrabold text-[13px] tracking-wide"
            style={{ background: "linear-gradient(135deg, #FF2E97, #A03CF5)" }}>
            {busy ? "…" : "GO LIVE"}
          </span>
        </button>
        <p className="text-white/70 text-[11px] mt-2.5 text-center">Earn rubies from gifts <span className="text-[#FFD86B]">✦</span></p>
      </div>
    </div>
  );
}
